import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import baseUrl from "../../../../../api/baseUrl";
import { RootState } from "../../../../../redux/store";
import {
  addIntraOralSoftTissue,
  removeIntraOralSoftTissue,
} from "../../../../../redux/diagnosestate/diagnoseAction";

const SOFT_TISSUE_OPTIONS = [
  "Buccal mucosa",
  "Lips",
  "Gingiva",
  "Floor of the mouth",
  "Tongue",
] as const;

interface FormData {
  type: string;
  note: string;
}

function IntraOralSoftTissues({ setmakeAction, makeAction, adult }: any) {
  const dispatch = useDispatch();
  const { ID } = useParams();

  const [formData, setformData] = useState<FormData>({
    type: "",
    note: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setformData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!formData.type.trim()) {
      toast.error("Please select a soft tissue type.");
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await baseUrl.patch(
        "/client/treatments/intraOralSoftTissue/create",
        { type: formData.type.trim(), note: formData.note.trim() },
        { headers: { treatmentid: ID } }
      );

      if (response.data?.success && response.data?.data) {
        dispatch(addIntraOralSoftTissue(response.data.data));
        setformData({ type: "", note: "" });
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to add entry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const intraOralSoftTissues = useSelector(
    (state: RootState) => state.diagnose.intraOralSoftTissues
  );

  const handleRemove = async (id: string) => {
    try {
      await baseUrl.patch(
        "/client/treatments/intraOralSoftTissue/remove",
        {},
        { headers: { treatmentid: ID, intraoralsofttissueid: id } }
      );
      dispatch(removeIntraOralSoftTissue(id));
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove entry.");
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">
          Intra oral examination – Soft tissues
        </h4>
      </div>

      <div className="p-4">
        <form
          onSubmit={handleSubmit}
          className="flex flex-wrap gap-3 items-end mb-4"
        >
          <div className="w-48">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">
              Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
            >
              <option value="">Select</option>
              {SOFT_TISSUE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">
              Note
            </label>
            <input
              name="note"
              type="text"
              value={formData.note}
              onChange={handleChange}
              placeholder="Enter note"
              className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400"
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium text-xs hover:shadow-lg hover:shadow-cyan-200 transition-all disabled:opacity-50"
          >
            {isSubmitting ? "Adding..." : "Add"}
          </button>
        </form>

        {intraOralSoftTissues?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                    Type
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                    Note
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase w-20">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {intraOralSoftTissues.map((item: any) => (
                  <tr key={item._id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200">
                      {item.type || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                      {item.note || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleRemove(item._id)}
                        className="px-3 py-1 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 text-xs font-medium transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(!intraOralSoftTissues || intraOralSoftTissues.length === 0) && (
          <div className="text-center py-4 text-slate-400 dark:text-slate-500">
            <p className="text-xs">No soft tissue entries added yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default IntraOralSoftTissues;
